import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Send } from "lucide-react";
import { api } from "../lib/api";
import { useStudio } from "../lib/context";
import { Drawer, errorMessage, fmtDate, Loading } from "./ui";

/**
 * Codes de connexion par Telegram : chaque membre relie sa propre conversation au bot, depuis son compte.
 * Le lien d'appairage vaut 10 minutes ; une fois relié, le code de connexion arrive sur Telegram au lieu du SMS.
 */
export function TelegramDialog({ onClose }: { onClose: () => void }) {
  const { toast } = useStudio();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["me-telegram"], queryFn: api.telegram, refetchInterval: (query) => (query.state.data && !query.state.data.linked ? 4000 : false) });
  const [link, setLink] = useState<string | null>(null);
  const start = useMutation({ mutationFn: api.telegramLink, onSuccess: (r) => { setLink(r.url); window.open(r.url, "_blank", "noopener"); }, onError: (e) => toast(errorMessage(e), "err") });
  const stop = useMutation({ mutationFn: api.telegramUnlink, onSuccess: () => { toast("Appairage retiré : vos codes repartent par SMS"); setLink(null); void qc.invalidateQueries({ queryKey: ["me-telegram"] }); }, onError: (e) => toast(errorMessage(e), "err") });
  const d = q.data;
  return (
    <Drawer title="Codes de connexion par Telegram" icon={<span className="icon-tile"><Send size={18} /></span>} onClose={onClose}
      subtitle="Recevez votre code de connexion sur Telegram au lieu du SMS. Le code reste à usage unique et valable 5 minutes.">
      {!d ? <Loading lines={3} /> : !d.available ? (
        <p className="muted">Aucun bot Telegram n'est réglé pour le studio. Un administrateur l'ajoute dans Système → Fournisseurs → Telegram.</p>
      ) : d.linked ? (
        <div className="stack" style={{ gap: 12 }}>
          <p>Votre compte est relié au bot <b>@{d.bot}</b>{d.linkedAt ? ` depuis le ${fmtDate(d.linkedAt)}` : ""}. Vos codes de connexion arrivent dans cette conversation.</p>
          <div><button className="btn ghost" disabled={stop.isPending} onClick={() => { if (confirm("Retirer l'appairage ? Vos codes repartiront par SMS.")) stop.mutate(); }}>Retirer l'appairage</button></div>
        </div>
      ) : (
        <div className="stack" style={{ gap: 12 }}>
          <ol className="small" style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
            <li>Ouvrez le lien ci-dessous : il lance la conversation avec le bot <b>@{d.bot}</b> dans Telegram.</li>
            <li>Touchez <b>Démarrer</b> (ou envoyez le message proposé). Le bot confirme l'appairage.</li>
            <li>Revenez ici : la page se met à jour toute seule.</li>
          </ol>
          <div className="row wrap" style={{ gap: 8 }}>
            <button className="btn" disabled={start.isPending} onClick={() => start.mutate()}><ExternalLink size={15} />{link ? "Rouvrir le lien" : "Relier mon compte Telegram"}</button>
            {link && <span className="small muted">Lien valable 10 minutes. En attente de l'appairage…</span>}
          </div>
        </div>
      )}
    </Drawer>
  );
}
